import React,{useState} from 'react'
import { useParams, useOutletContext } from 'react-router-dom';
import clinicService from '../../services/clinic.service';
import Grid from "@mui/material/Grid2";
import {
    Paper,
    Typography,
    Box,
    Stepper,
    Step,
    StepLabel,
    Button,
    Card,
    CardMedia,
    CardActionArea,
    TextField,
    useTheme,
    useMediaQuery,
    CircularProgress,
    IconButton,
    Alert,
    Avatar,
    Checkbox,
    FormControlLabel,
    Chip,
    Divider,
    Stack,
    Autocomplete,
  } from '@mui/material';
import "../reputation/reputation.css";
import "./welcome.css";
import "./appointmentBooking.css";
  import { LocalizationProvider, DateCalendar } from '@mui/x-date-pickers';
  import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
  import { format, addDays } from 'date-fns';
  import ArrowBackIcon from '@mui/icons-material/ArrowBack';
  import CheckCircleIcon from '@mui/icons-material/CheckCircle';
  import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
  import AccessTimeIcon from '@mui/icons-material/AccessTime';
  import EventAvailableIcon from '@mui/icons-material/EventAvailable';
  import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
  import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
  import ChevronRightIcon from '@mui/icons-material/ChevronRight';
  import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile';

const WEEKDAY_NAMES = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const isClinicClosedOn = (date, hours) => {
  if (!date || !Array.isArray(hours) || hours.length === 0) {
    return false;
  }
  const dayName = WEEKDAY_NAMES[date.getDay()];
  const hour = hours.find((entry) => String(entry.day || '').toUpperCase() === dayName);
  if (!hour) {
    return false;
  }
  if (typeof hour.status === 'boolean') {
    return hour.status === false;
  }
  return String(hour.workHours || '').trim().toLowerCase() === 'closed';
};

const getNextOpenDate = (fromDate, hours) => {
  let candidate = fromDate;
  for (let i = 0; i < 14; i += 1) {
    if (!isClinicClosedOn(candidate, hours)) {
      return candidate;
    }
    candidate = addDays(candidate, 1);
  }
  return fromDate;
};

function DoctorBio({ text, expanded, onToggle, mutedColor, inkColor }) {
  const textRef = React.useRef(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useLayoutEffect(() => {
    if (expanded || !textRef.current) {
      setIsTruncated(false);
      return;
    }
    const el = textRef.current;
    setIsTruncated(el.scrollHeight > el.clientHeight + 1);
  }, [text, expanded]);

  if (!text) {
    return null;
  }

  return (
    <Box sx={{ mt: 0.25 }}>
      <Typography
        ref={textRef}
        variant="body2"
        sx={{
          color: mutedColor,
          wordBreak: 'break-word',
          ...(expanded
            ? { whiteSpace: 'pre-wrap' }
            : {
                display: '-webkit-box',
                WebkitLineClamp: 5,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }),
        }}
      >
        {text}
      </Typography>
      {(expanded || isTruncated) && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onToggle();
          }}
          sx={{
            mt: 0.25,
            p: 0,
            border: 0,
            background: 'none',
            cursor: 'pointer',
            color: inkColor,
            fontWeight: 700,
            font: 'inherit',
            fontSize: '0.8125rem',
            textDecoration: 'underline',
            textUnderlineOffset: '2px',
            '&:hover': { opacity: 0.8 },
          }}
        >
          {expanded ? 'Show less' : 'Read more'}
        </Typography>
      )}
    </Box>
  );
}

function AppointmentBooking() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const { clinicContent } = useOutletContext() || {};
    const clinicHours = clinicContent?.hours;
    const [activeStep, setActiveStep] = useState(0);
    const steps = ['Select Exam Type', 'Choose Date & Doctor', 'Select Time Slot', 'Patient Information'];
    const [selectedExamType, setSelectedExamType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(() => getNextOpenDate(addDays(new Date(), 7), []));
    const [availableDoctors, setAvailableDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [expandedBioIds, setExpandedBioIds] = useState({});
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [submitError, setSubmitError] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);
  const turnstileRequired = isTurnstileConfigured();
    // Fields mirror the booking_appointment_request table. Optional columns are optional here too.
    const [patientInfo, setPatientInfo] = useState({
        firstName: '',      // required
        lastName: '',       // required
        preferredName: '',  // optional
        email: '',          // required
        cellPhone: '',      // required
        homePhone: '',      // optional
        reason: [],         // optional multi-select visit reasons
        comments: '',       // optional
        isExistingPatient: false, // maps to !isNewPatient
        website: '',        // honeypot: must stay empty (spam trap)
      });
      // When the patient reached the info form; used to detect implausibly fast (bot) submissions.
      const formStartedAtRef = React.useRef(null);
      const { clinicId } = useParams();
      const [examTypes, setExamTypes] = useState([]);
      const [examTypesLoading, setExamTypesLoading] = useState(true);
      const [visitReasonOptions, setVisitReasonOptions] = useState([]);

  // Once clinic hours are available, move the default date off any closed day.
  React.useEffect(() => {
    if (!clinicHours?.length) {
      return;
    }
    setSelectedDate((current) => getNextOpenDate(current || addDays(new Date(), 7), clinicHours));
  }, [clinicHours]);

  // Fetch bookable appointment types for the clinic.
  React.useEffect(() => {
    if (!clinicId) return;

    setExamTypesLoading(true);
    clinicService
      .getBookingTypes(clinicId)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const mapped = list
          .filter((t) => t.isActive !== false)
          .map((t) => ({
            id: t.id,
            name: t.apptType,
            timeSlot: t.timeSlot,
            description: t.timeSlot ? `${t.timeSlot} minute appointment` : 'Appointment',
          }));
        setExamTypes(mapped);
      })
      .catch((err) => {
        // 404 = no appointment types configured for the clinic.
        // eslint-disable-next-line no-console
        console.log('Failed to load appointment types', err);
        setExamTypes([]);
      })
      .finally(() => setExamTypesLoading(false));
  }, [clinicId]);

  // Fetch clinic visit reasons for the patient information step.
  React.useEffect(() => {
    if (!clinicId) return;

    clinicService
      .getVisitReasons(clinicId)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setVisitReasonOptions(
          list
            .filter((reason) => reason.isActive !== false)
            .map((reason) => reason.reason)
            .filter(Boolean)
        );
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log('Failed to load visit reasons', err);
        setVisitReasonOptions([]);
      });
  }, [clinicId]);

  // Retrieve doctors that offer the selected appointment type AND are working on the selected date.
  React.useEffect(() => {
    if (selectedExamType && selectedDate && activeStep === 1) {
      fetchDoctorsForApptType(selectedExamType.id, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedExamType, selectedDate, activeStep]);

  // Re-fetch slots whenever the doctor OR the viewed date changes while on the time-slot step.
  // This lets the patient hop between dates and pick slots from different days.
  React.useEffect(() => {
    if (selectedDoctor && activeStep === 2) {
      fetchAvailableSlotsForDoctor(selectedDoctor, selectedDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctor, selectedDate, activeStep]);

  // Stamp when the patient lands on the Patient Information step (for bot-speed detection).
  React.useEffect(() => {
    if (activeStep === 3 && formStartedAtRef.current === null) {
      formStartedAtRef.current = Date.now();
    }
  }, [activeStep]);

  const fetchDoctorsForApptType = (apptTypeId, date) => {
    if (!clinicId || !apptTypeId || !date) return;
    setLoading(true);
    const dateStr = format(date, 'yyyy-MM-dd');
    clinicService
      .getBookingDoctors(clinicId, apptTypeId, dateStr)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const mapped = list.map((doc) => ({
          id: doc.id,
          name: doc.name,
          specialty: doc.bio || '',
          photo: doc.photoUrl,
        }));
        setAvailableDoctors(mapped);
      })
      .catch((err) => {
        // 404 = no doctors offer this appointment type.
        // eslint-disable-next-line no-console
        console.log('Failed to load doctors', err);
        setAvailableDoctors([]);
      })
      .finally(() => setLoading(false));
  };

  // Format a "HH:mm" 24-hour string into a "hh:mm AM/PM" label.
  const formatSlotLabel = (time) => {
    if (!time) return '';
    const [hStr, mStr] = time.split(':');
    const hour = parseInt(hStr, 10);
    const minute = mStr ?? '00';
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 === 0 ? 12 : hour % 12;
    return `${String(hour12).padStart(2, '0')}:${minute} ${period}`;
  };

  const fetchAvailableSlotsForDoctor = (doctor, date) => {
    if (!clinicId || !doctor?.id || !selectedExamType?.id || !date) return;
    setLoading(true);
    const dateStr = format(date, 'yyyy-MM-dd');
    clinicService
      .getAvailableSlots(clinicId, selectedExamType.id, doctor.id, dateStr)
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        const mapped = list.map((slot) => ({
          // id must be unique across dates so the same time on two days can both be selected.
          id: `${dateStr}_${slot.startTime}`,
          time: formatSlotLabel(slot.startTime),
          startTime: slot.startTime,
          endTime: slot.endTime,
          available: slot.available !== false,
          date: date,
          dateStr,
          dateLabel: format(date, 'MMMM d, yyyy'),
          doctorId: doctor.id,
          doctorName: doctor.name,
        }));
        setAvailableSlots(mapped);
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log('Failed to load available slots', err);
        setAvailableSlots([]);
      })
      .finally(() => setLoading(false));
  };

  // Event handlers
  const handleExamTypeSelect = (examType) => {
    setSelectedExamType(examType);
    setSelectedDate(getNextOpenDate(addDays(new Date(), 7), clinicHours));
    setSelectedDoctor(null);
    setSelectedSlots([]);
    setExpandedBioIds({});
    handleNext();
  };

  const handleDateChange = (newDate) => {
    if (isClinicClosedOn(newDate, clinicHours)) {
      return;
    }
    setSelectedDate(newDate);
    setSelectedDoctor(null);
    setSelectedSlots([]);
  };

  const handleDoctorSelect = (doctor) => {
    // Both preferred slots must belong to the same doctor. If the patient switches
    // to a different doctor, drop any slots that were picked for the previous one.
    setSelectedSlots((prev) =>
      prev.length > 0 && prev[0].doctorId !== doctor.id ? [] : prev
    );
    setSelectedDoctor(doctor);
    handleNext();
  };

  // Changing the date on the time-slot step keeps already-selected slots (they may be on other dates).
  const handleSlotDateChange = (newDate) => {
    if (isClinicClosedOn(newDate, clinicHours)) {
      return;
    }
    setSelectedDate(newDate);
  };

  const handleSlotToggle = (slot) => {
    if (!slot.available) return;

    if (selectedSlots.find(s => s.id === slot.id)) {
      setSelectedSlots(selectedSlots.filter(s => s.id !== slot.id));
    } else {
      // Guard: never mix slots from different doctors.
      if (selectedSlots.length > 0 && selectedSlots[0].doctorId !== slot.doctorId) return;
      if (selectedSlots.length < 2) {
        setSelectedSlots([...selectedSlots, slot]);
      }
    }
  };

  // Mask a phone value into "(###) ###-####" as the user types.
  const formatPhoneNumber = (value) => {
    const digits = (value || '').replace(/\D/g, '').slice(0, 10);
    const len = digits.length;
    if (len === 0) return '';
    if (len < 4) return `(${digits}`;
    if (len < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const isValidPhone = (phone) => /^\(\d{3}\) \d{3}-\d{4}$/.test(phone);

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    setPatientInfo((prev) => ({ ...prev, [name]: formatPhoneNumber(value) }));
  };

  // "July 24, 2026 at 10:00 AM & July 25, 2026 at 02:30 PM"
  const formatSelectedSlots = () =>
    selectedSlots
      .map((s) => `${s.dateLabel || format(s.date, 'MMMM d, yyyy')} at ${s.time}`)
      .join(' & ');

  const handlePatientInfoChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPatientInfo({
      ...patientInfo,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Assemble the booking_appointment_request payload from the wizard selections + form.
  const buildAppointmentRequest = () => {
    const [first, second] = selectedSlots;
    // second_preferred_* is NOT NULL in the table; if only one slot was picked, reuse the first.
    const secondSlot = second || first;
    return {
      bookingDoctorId: selectedDoctor?.id,
      bookingAppointmentType: selectedExamType?.id ? { id: selectedExamType.id } : null,
      isNewPatient: !patientInfo.isExistingPatient,
      firstName: patientInfo.firstName,
      lastName: patientInfo.lastName,
      preferredName: patientInfo.preferredName || null,
      email: patientInfo.email,
      cellPhone: patientInfo.cellPhone,
      homePhone: patientInfo.homePhone || null,
      firstPreferredDate: first ? format(first.date, 'yyyy-MM-dd') : null,
      firstPreferredTime: first ? first.time : null,
      secondPreferredDate: secondSlot ? format(secondSlot.date, 'yyyy-MM-dd') : null,
      secondPreferredTime: secondSlot ? secondSlot.time : null,
      reason: patientInfo.reason.length ? patientInfo.reason.join(', ') : null,
      comments: patientInfo.comments || null,
      // Anti-spam signals (see BookingServiceImpl): honeypot must be empty, elapsedMs = time on form.
      website: patientInfo.website || '',
      elapsedMs: formStartedAtRef.current ? Date.now() - formStartedAtRef.current : null,
      turnstileToken: turnstileToken || null,
    };
  };

  const handleSubmit = () => {
    if (!clinicId) return;
    setLoading(true);
    setSubmitError('');
    const appointmentRequest = buildAppointmentRequest();
    clinicService
      .createAppointmentRequest(clinicId, appointmentRequest)
      .then(() => {
        handleNext();
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log('Failed to submit appointment request', err);
        const message =
          err?.response?.data?.message ||
          'We could not submit your appointment request. Please try again.';
        setSubmitError(message);
        // Turnstile tokens are single-use — force a fresh challenge after failure.
        setTurnstileToken('');
        setTurnstileResetKey((k) => k + 1);
      })
      .finally(() => setLoading(false));
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setSelectedExamType(null);
    setSelectedDate(getNextOpenDate(addDays(new Date(), 7), clinicHours));
    setSelectedDoctor(null);
    setSelectedSlots([]);
    setSubmitError('');
    setTurnstileToken('');
    setTurnstileResetKey((k) => k + 1);
    setPatientInfo({
      firstName: '',
      lastName: '',
      preferredName: '',
      email: '',
      cellPhone: '',
      homePhone: '',
      reason: [],
      comments: '',
      isExistingPatient: false,
      website: '',
    });
    formStartedAtRef.current = null;
  };

  // Shared design tokens so accents follow the clinic's brand color (falls back to EyeconX green).
  const ACCENT = 'var(--rep-accent, var(--primary-color, #2e8b57))';
  const INK = 'var(--rep-ink, #15252e)';
  const MUTED = 'var(--rep-muted, #5a6b74)';
  const LINE = 'var(--rep-line, rgba(0,0,0,0.12))';
  const accentButtonSx = {
    bgcolor: ACCENT,
    textTransform: 'none',
    fontWeight: 600,
    borderRadius: 2,
    px: 3,
    py: 1,
    boxShadow: 'none',
    '&:hover': { bgcolor: ACCENT, filter: 'brightness(0.94)', boxShadow: 'none' },
  };

  // Consistent header across steps: optional back button + step counter + title + subtitle.
  const renderStepHeader = (title, subtitle, { showBack = true } = {}) => (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {showBack && (
          <IconButton onClick={handleBack} size="small" aria-label="Go back"
            sx={{ border: `1px solid ${LINE}`, color: INK }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        )}
        <Box>
          <Typography sx={{ color: MUTED, fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Step {activeStep + 1} of {steps.length}
          </Typography>
          <Typography variant="h6" sx={{ color: INK, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.2 }}>
            {title}
          </Typography>
        </Box>
      </Box>
      {subtitle && (
        <Typography variant="body2" sx={{ color: MUTED, mt: 1 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );

// UI Components for each step
const renderExamTypeSelection = () => (
    <Box>
      {renderStepHeader(
        'What type of exam do you need?',
        'Select the eye examination that best fits your visit.',
        { showBack: false }
      )}

      {examTypesLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" height="220px">
          <CircularProgress sx={{ color: ACCENT }} />
        </Box>
      ) : examTypes.length === 0 ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Online booking is not available for this clinic at the moment. Please contact the clinic directly.
        </Alert>
      ) : (
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          {examTypes.map((examType) => (
            <Grid size={{xs:12,sm:6,md:4}} key={examType.id}>
              <Card
                elevation={0}
                sx={{
                  height: '100%',
                  border: `1px solid ${LINE}`,
                  borderRadius: 3,
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease, border-color 0.18s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 28px rgba(20,40,30,0.12)',
                    borderColor: ACCENT,
                  }
                }}
              >
                <CardActionArea
                  onClick={() => handleExamTypeSelect(examType)}
                  sx={{ height: '100%', p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1.5 }}
                >
                  <Box
                    sx={{
                      width: 52, height: 52, borderRadius: '14px',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: 'rgba(46,139,87,0.10)', color: ACCENT,
                    }}
                  >
                    <VisibilityOutlinedIcon />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: INK }}>
                      {examType.name}
                    </Typography>
                    {examType.timeSlot && (
                      <Typography variant="body2" sx={{ color: MUTED, display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                        <AccessTimeIcon sx={{ fontSize: '0.95rem' }} /> {examType.timeSlot} minutes
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: ACCENT, fontWeight: 600, fontSize: '0.85rem' }}>
                    Select <ChevronRightIcon fontSize="small" />
                  </Box>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
  const renderDateAndDoctorSelection = () => (
    <Box>
      {renderStepHeader(
        'Choose a date & doctor',
        `${selectedExamType?.name || 'Appointment'} · pick a day, then select an available provider.`
      )}

      <Grid container spacing={2.5} sx={{ alignItems: 'flex-start', flexWrap: { md: 'nowrap' } }}>
        <Grid size={{ xs: 12, md: 'auto' }} className="apb-datepicker-col">
          <Paper elevation={0} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, p: 1, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700, px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarMonthIcon fontSize="small" sx={{ color: ACCENT }} /> Appointment date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={handleDateChange}
                disablePast
                shouldDisableDate={(date) => isClinicClosedOn(date, clinicHours)}
                sx={{ '& .MuiPickersDay-root.Mui-selected': { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT } } }}
              />
            </LocalizationProvider>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 'grow' }} className="apb-doctors-col">
          <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700, mb: 1.5 }}>
            Available doctors{selectedDate ? ` · ${format(selectedDate, 'EEE, MMM d')}` : ''}
          </Typography>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="220px">
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : availableDoctors.length > 0 ? (
            <Stack spacing={1.5}>
              {availableDoctors.map((doctor) => (
                <Card
                  key={doctor.id}
                  elevation={0}
                  sx={{
                    border: `1px solid ${LINE}`,
                    borderRadius: 3,
                    transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 20px rgba(20,40,30,0.10)',
                      borderColor: ACCENT,
                    }
                  }}
                >
                  <CardActionArea onClick={() => handleDoctorSelect(doctor)} sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                      {doctor.photo ? (
                        <CardMedia
                          component="img"
                          sx={{ width: 64, height: 64, borderRadius: '50%', objectFit: 'cover' }}
                          image={doctor.photo}
                          alt={doctor.name}
                        />
                      ) : (
                        <Avatar sx={{ width: 64, height: 64, bgcolor: 'rgba(46,139,87,0.12)', color: ACCENT, fontWeight: 700 }}>
                          {doctor.name ? doctor.name.charAt(0) : '?'}
                        </Avatar>
                      )}
                      <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: INK }}>
                          {doctor.name}
                        </Typography>
                        {doctor.specialty && (
                          <DoctorBio
                            text={doctor.specialty}
                            expanded={!!expandedBioIds[doctor.id]}
                            mutedColor={MUTED}
                            inkColor={INK}
                            onToggle={() => {
                              setExpandedBioIds((prev) => ({
                                ...prev,
                                [doctor.id]: !prev[doctor.id],
                              }));
                            }}
                          />
                        )}
                      </Box>
                      <ChevronRightIcon sx={{ color: MUTED }} />
                    </Box>
                  </CardActionArea>
                </Card>
              ))}
            </Stack>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No doctors are working on this date for this appointment type. Please choose another date.
            </Alert>
          )}
        </Grid>
      </Grid>
    </Box>
  );

  const renderTimeSlotSelection = () => (
    <Box>
      {renderStepHeader(
        'Pick your preferred times',
        `Select up to 2 time slots (${selectedExamType?.timeSlot || 30} minutes each) with ${selectedDoctor?.name || 'this doctor'}. You can choose slots on different dates.`
      )}

      {selectedSlots.length > 0 && (
        <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: 'rgba(46,139,87,0.05)' }}>
          <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700, mb: 1 }}>
            Selected times ({selectedSlots.length}/2)
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {selectedSlots.map((s) => (
              <Chip
                key={s.id}
                icon={<AccessTimeIcon />}
                label={`${s.dateLabel} · ${s.time}`}
                onDelete={() => handleSlotToggle(s)}
                sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 600,
                      '& .MuiChip-icon': { color: '#fff' },
                      '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.8)', '&:hover': { color: '#fff' } } }}
              />
            ))}
          </Box>
        </Paper>
      )}

      <Grid container spacing={2.5} sx={{ alignItems: 'flex-start', flexWrap: { md: 'nowrap' } }}>
        <Grid size={{ xs: 12, md: 'auto' }} className="apb-datepicker-col">
          <Paper elevation={0} sx={{ border: `1px solid ${LINE}`, borderRadius: 3, p: 1, overflow: 'hidden' }}>
            <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700, px: 1.5, pt: 1, display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <CalendarMonthIcon fontSize="small" sx={{ color: ACCENT }} /> Change date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateCalendar
                value={selectedDate}
                onChange={handleSlotDateChange}
                disablePast
                shouldDisableDate={(date) => isClinicClosedOn(date, clinicHours)}
                sx={{ '& .MuiPickersDay-root.Mui-selected': { bgcolor: ACCENT, '&:hover': { bgcolor: ACCENT } } }}
              />
            </LocalizationProvider>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12, md: 'grow' }} className="apb-doctors-col">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'rgba(46,139,87,0.12)', color: ACCENT, fontSize: '0.9rem', fontWeight: 700 }}>
              {selectedDoctor?.name ? selectedDoctor.name.charAt(0) : '?'}
            </Avatar>
            <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700 }}>
              {selectedDoctor?.name} &middot; {format(selectedDate, 'EEE, MMM d')}
            </Typography>
          </Box>

          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" height="220px">
              <CircularProgress sx={{ color: ACCENT }} />
            </Box>
          ) : availableSlots.length > 0 ? (
            <Grid container spacing={1.25}>
              {availableSlots.map((slot) => {
                const isSelected = Boolean(selectedSlots.find(s => s.id === slot.id));
                const disabled = !slot.available || (!isSelected && selectedSlots.length >= 2);
                return (
                  <Grid size={{xs:6,sm:4,md:4,lg:3}} key={slot.id}>
                    <Box
                      role="button"
                      onClick={() => !disabled && handleSlotToggle(slot)}
                      sx={{
                        position: 'relative',
                        textAlign: 'center',
                        py: 1.25, px: 1,
                        borderRadius: 2,
                        border: `1.5px solid ${isSelected ? ACCENT : LINE}`,
                        bgcolor: isSelected ? ACCENT : '#fff',
                        color: isSelected ? '#fff' : INK,
                        fontWeight: 600,
                        opacity: disabled && !isSelected ? 0.45 : 1,
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': !disabled ? { borderColor: ACCENT, transform: 'translateY(-1px)' } : {},
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {slot.time}
                      </Typography>
                      {isSelected && (
                        <CheckCircleIcon sx={{ position: 'absolute', top: 4, right: 4, fontSize: '1rem', color: '#fff' }} />
                      )}
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Alert severity="info" sx={{ borderRadius: 2 }}>
              No available slots for this date. Please choose another date on the left.
            </Alert>
          )}
        </Grid>
      </Grid>

      <Divider sx={{ mt: 3, mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disableElevation
          disabled={selectedSlots.length === 0}
          onClick={handleNext}
          endIcon={<ChevronRightIcon />}
          sx={accentButtonSx}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );

  const renderPatientInformationForm = () => {
    const emailInvalid = patientInfo.email !== '' && !isValidEmail(patientInfo.email);
    const cellPhoneInvalid = patientInfo.cellPhone !== '' && !isValidPhone(patientInfo.cellPhone);
    const homePhoneInvalid = patientInfo.homePhone !== '' && !isValidPhone(patientInfo.homePhone);
    const canSubmit =
      patientInfo.firstName &&
      patientInfo.lastName &&
      isValidEmail(patientInfo.email) &&
      isValidPhone(patientInfo.cellPhone) &&
      !homePhoneInvalid &&
      (!turnstileRequired || Boolean(turnstileToken));

    return (
    <Box>
      {renderStepHeader('Your details', 'Tell us who the appointment is for. Fields marked with * are required.')}

      {/* Booking summary */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 3, border: `1px solid ${LINE}`, bgcolor: 'rgba(46,139,87,0.05)' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <EventAvailableIcon sx={{ color: ACCENT, mt: '2px' }} />
          <Box>
            <Typography variant="subtitle2" sx={{ color: INK, fontWeight: 700 }}>
              {selectedExamType?.name} with {selectedDoctor?.name}
            </Typography>
            <Typography variant="body2" sx={{ color: MUTED }}>
              {formatSelectedSlots()}
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 3, border: `1px solid ${LINE}` }}>
        {/* Honeypot: hidden from real users; bots that fill it are rejected server-side. */}
        <input
          type="text"
          name="website"
          value={patientInfo.website}
          onChange={handlePatientInfoChange}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
        />

        <Typography variant="overline" sx={{ color: MUTED, fontWeight: 700, letterSpacing: '0.1em' }}>
          <PersonOutlineIcon sx={{ fontSize: '1rem', verticalAlign: 'middle', mr: 0.5 }} />
          Patient details
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid size={{xs:12,sm:6}}>
            <TextField required fullWidth label="First Name" name="firstName"
              value={patientInfo.firstName} onChange={handlePatientInfoChange}
              inputProps={{ maxLength: 75 }} />
          </Grid>
          <Grid size={{xs:12,sm:6}}>
            <TextField required fullWidth label="Last Name" name="lastName"
              value={patientInfo.lastName} onChange={handlePatientInfoChange}
              inputProps={{ maxLength: 75 }} />
          </Grid>
          <Grid size={{xs:12,sm:6}}>
            <TextField fullWidth label="Preferred Name" name="preferredName"
              value={patientInfo.preferredName} onChange={handlePatientInfoChange}
              helperText="Optional" inputProps={{ maxLength: 75 }} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" sx={{ color: MUTED, fontWeight: 700, letterSpacing: '0.1em' }}>
          Contact information
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid size={{xs:12,sm:6}}>
            <TextField required fullWidth label="Email" name="email" type="email"
              value={patientInfo.email} onChange={handlePatientInfoChange}
              error={emailInvalid} helperText={emailInvalid ? 'Enter a valid email address' : ' '}
              inputProps={{ maxLength: 75 }} />
          </Grid>
          <Grid size={{xs:12,sm:6}} />
          <Grid size={{xs:12,sm:6}}>
            <TextField required fullWidth label="Cell Phone" name="cellPhone"
              value={patientInfo.cellPhone} onChange={handlePhoneChange}
              error={cellPhoneInvalid} helperText={cellPhoneInvalid ? 'Format: (###) ###-####' : ' '}
              placeholder="(###) ###-####" inputProps={{ maxLength: 14 }} />
          </Grid>
          <Grid size={{xs:12,sm:6}}>
            <TextField fullWidth label="Home Phone" name="homePhone"
              value={patientInfo.homePhone} onChange={handlePhoneChange}
              error={homePhoneInvalid} helperText={homePhoneInvalid ? 'Format: (###) ###-####' : 'Optional'}
              placeholder="(###) ###-####" inputProps={{ maxLength: 14 }} />
          </Grid>
        </Grid>

        <Divider sx={{ my: 3 }} />

        <Typography variant="overline" sx={{ color: MUTED, fontWeight: 700, letterSpacing: '0.1em' }}>
          Visit details
        </Typography>
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid size={{xs:12}}>
            <Autocomplete
              multiple
              options={visitReasonOptions}
              value={patientInfo.reason}
              onChange={(_, selected) => {
                setPatientInfo((prev) => ({ ...prev, reason: selected }));
              }}
              disableCloseOnSelect
              noOptionsText="No visit reasons available"
              renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option}
                    label={option}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(46, 139, 87, 0.12)',
                      color: INK,
                      '& .MuiChip-deleteIcon': { color: MUTED },
                    }}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Reason for Visit"
                  helperText="Optional — select one or more"
                  placeholder={patientInfo.reason.length ? '' : 'Select reasons'}
                />
              )}
            />
          </Grid>
          <Grid size={{xs:12}}>
            <TextField fullWidth label="Comments or Special Requirements" name="comments" multiline rows={3}
              value={patientInfo.comments} onChange={handlePatientInfoChange}
              helperText="Optional" inputProps={{ maxLength: 255 }} />
          </Grid>
          <Grid size={{xs:12}}>
            <FormControlLabel
              control={
                <Checkbox name="isExistingPatient" checked={patientInfo.isExistingPatient}
                  onChange={handlePatientInfoChange} sx={{ '&.Mui-checked': { color: ACCENT } }} />
              }
              label="I am an existing patient of this clinic"
            />
          </Grid>
          {turnstileRequired && (
            <Grid size={{xs:12}}>
              <Turnstile
                resetKey={turnstileResetKey}
                onSuccess={(token) => setTurnstileToken(token)}
                onExpire={() => setTurnstileToken('')}
                onError={() => setTurnstileToken('')}
              />
            </Grid>
          )}
        </Grid>
      </Paper>

      {submitError && (
        <Box sx={{ mt: 3 }}>
          <Alert severity="error" onClose={() => setSubmitError('')} sx={{ borderRadius: 2 }}>{submitError}</Alert>
        </Box>
      )}

      <Divider sx={{ mt: 3, mb: 2 }} />
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          disableElevation
          onClick={handleSubmit}
          disabled={loading || !canSubmit}
          sx={accentButtonSx}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Request Appointment"}
        </Button>
      </Box>
    </Box>
    );
  };

  const renderThankYouMessage = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          width: 88, height: 88, borderRadius: '50%', mb: 3,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(46,139,87,0.12)', color: ACCENT,
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 52 }} />
      </Box>

      <Typography variant="h5" sx={{ fontWeight: 700, color: INK, mb: 1 }}>
        Appointment request submitted
      </Typography>

      <Typography variant="body1" sx={{ color: MUTED, maxWidth: 560, mb: 2 }}>
        This is a request only &mdash; your appointment is not confirmed yet. A member of our
        staff will call you to confirm the details and finalize your appointment. You don't
        need to do anything further for now.
      </Typography>

      <Alert severity="info" sx={{ maxWidth: 560, width: '100%', borderRadius: 2, mb: 3, textAlign: 'left' }}>
        Your preferred times below are not guaranteed until our staff confirms them by phone.
      </Alert>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: `1px solid ${LINE}`, maxWidth: 560, width: '100%', mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: INK }}>
          {selectedExamType?.name} with {selectedDoctor?.name}
        </Typography>
        <Typography variant="caption" sx={{ color: MUTED, textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700 }}>
          Requested times
        </Typography>
        <Typography variant="body1" sx={{ color: INK, mt: 0.5 }}>
          {formatSelectedSlots()}
        </Typography>
      </Paper>

      <Button
        variant="outlined"
        onClick={handleReset}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2, borderColor: ACCENT, color: ACCENT,
              '&:hover': { borderColor: ACCENT, bgcolor: 'rgba(46,139,87,0.06)' } }}
      >
        Book another appointment
      </Button>
    </Box>
  );


  return (
    <div className="welcome-stack apb-stack">
      <section className="rep-panel apb-header">
        <h2 className="rep-title apb-header__title">Book an appointment</h2>

        {activeStep < steps.length && (
          <Stepper
            activeStep={activeStep}
            alternativeLabel={!isMobile}
            orientation="horizontal"
            sx={{
              mt: 2,
              '& .MuiStepIcon-root.Mui-active': { color: ACCENT },
              '& .MuiStepIcon-root.Mui-completed': { color: ACCENT },
              '& .MuiStepLabel-label.Mui-active': { color: INK, fontWeight: 600 },
              '& .MuiStepLabel-label.Mui-completed': { color: INK },
            }}
          >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{!isTablet ? label : ''}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}
      </section>

      <section className="rep-panel">
        {activeStep === 0 && renderExamTypeSelection()}
        {activeStep === 1 && renderDateAndDoctorSelection()}
        {activeStep === 2 && renderTimeSlotSelection()}
        {activeStep === 3 && renderPatientInformationForm()}
        {activeStep === 4 && renderThankYouMessage()}
      </section>
    </div>
  )
}

export default AppointmentBooking